// TCIO AI visual language — the reusable design-system layer (Phase 4).
// Rule: the sparkle + AI blue appear ONLY on AI-generated/derived content
// and AI entry points. Never decorate ordinary stored record data with these.
import React, { useState, useEffect } from 'react';
import { tokens } from '../theme';

export const AI_SPARK = '\u2726'; // ✦

// Inline badge marking AI-derived content: ✦ + label in AI blue.
export function AiBadge({ label = 'TCIO', style }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: 12, fontWeight: 600, color: tokens.ai, fontFamily: tokens.font,
      letterSpacing: '0.04em', ...style }}>
      <span aria-hidden="true">{AI_SPARK}</span>{label}
    </span>
  );
}

// TCIO INSIGHTS strip — real data only. States: loading (quiet skeleton),
// data (cards), empty/error (renders NOTHING — never a fabricated count).
export function TcioInsights({ facilityId, api, token, onNavigate }) {
  const [state, setState] = useState({ phase: 'loading', counts: null });

  useEffect(() => {
    let dead = false;
    const url = api + '/api/due-counts' + (facilityId ? '?facilityId=' + facilityId : '');
    fetch(url, { headers: { Authorization: 'Bearer ' + token } })
      .then(r => r.json())
      .then(d => { if (!dead) setState(d.ok ? { phase: 'data', counts: d.counts } : { phase: 'hidden' }); })
      .catch(() => { if (!dead) setState({ phase: 'hidden' }); });
    return () => { dead = true; };
  }, [facilityId, api, token]);

  if (state.phase === 'hidden') return null;

  const CARDS = state.phase === 'data' ? [
    { n: state.counts.cosigns_pending, label: 'Cosigns Pending', sub: 'Awaiting signature', color: tokens.ai },
    { n: state.counts.notes_kicked_back, label: 'Notes Kicked Back', sub: 'Need revision', color: tokens.danger },
    { n: state.counts.compliance_gate, label: 'At Compliance Gate', sub: 'Blocked from finalizing', color: tokens.warning },
    { n: state.counts.auths_lapsed, label: 'Auths Lapsed', sub: 'No renewal sent', color: tokens.danger },
    { n: state.counts.auths_expiring_7d, label: 'Auths Expiring', sub: 'Within 7 days', color: '#F97316' },
  ].filter(c => c.n > 0) : [];

  if (state.phase === 'data' && CARDS.length === 0) return null;

  return (
    <div style={{ background: tokens.surface, border: '1px solid ' + tokens.border,
      borderRadius: 10, padding: '12px 16px', marginBottom: 16, boxShadow: tokens.shadow }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <AiBadge label="TCIO INSIGHTS" />
      </div>
      {state.phase === 'loading' ? (
        <div style={{ display: 'flex', gap: 10 }}>
          {[0,1,2].map(i => (
            <div key={i} style={{ flex: 1, height: 44, borderRadius: 8,
              background: tokens.surfaceSoft, border: '1px solid ' + tokens.borderSoft }} />
          ))}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {CARDS.map(c => (
            <div key={c.label} style={{ flex: '1 1 160px', display: 'flex', alignItems: 'center', gap: 10,
              border: '1px solid ' + tokens.borderSoft, borderRadius: 8, padding: '9px 13px',
              background: tokens.surfaceSoft }}>
              <span style={{ fontSize: 20, fontWeight: 600, color: c.color, fontVariantNumeric: 'tabular-nums' }}>{c.n}</span>
              <span style={{ lineHeight: 1.25 }}>
                <span style={{ display: 'block', fontSize: 13, fontWeight: 500, color: tokens.ink }}>{c.label}</span>
                <span style={{ display: 'block', fontSize: 12, color: tokens.textSecondary }}>{c.sub}</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Ask TCIO entry point — UI structure only; transparently not yet live.
export function AskTcioBar({ style }) {
  return (
    <button onClick={() => alert('Ask TCIO is coming soon \u2014 it will answer from your live TCIO data.')}
      style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%',
        background: tokens.aiSoft, border: '1px solid ' + tokens.border, borderRadius: 9,
        padding: '9px 14px', fontSize: 13.5, color: tokens.ai, fontWeight: 500,
        fontFamily: tokens.font, cursor: 'pointer', textAlign: 'left', ...style }}>
      <span aria-hidden="true">{AI_SPARK}</span> Ask TCIO about this page…
    </button>
  );
}
